import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown, AlertTriangle, AlertCircle } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Progress } from '@/client/components/ui/progress';
import { Badge } from '@/client/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';
import type { TranscodeJob, TrackedJob, StallStatus } from '@shared/types';
import { ClientIDCell, EventIDCell, EventLinksCell, CopyableCell } from './cells';

// Type guard to check if job is a TrackedJob (has stall detection fields)
function isTrackedJob(job: TranscodeJob): job is TrackedJob {
  return 'stallStatus' in job;
}

function StallIndicator({ stallStatus }: { stallStatus: StallStatus }) {
  if (stallStatus === 'normal') return null;

  if (stallStatus === 'stalled') {
    return (
      <Tooltip>
        <TooltipTrigger>
          <Badge variant="outline" className="border-amber-500 text-amber-600 gap-1">
            <AlertTriangle className="h-3 w-3" />
            Stalled
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>No progress for 5+ minutes</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (stallStatus === 'silent-failure') {
    return (
      <Tooltip>
        <TooltipTrigger>
          <Badge variant="destructive" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            Silent Failure
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>Stuck at 100% for 10+ minutes without completing</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return null;
}

function JobStatus({ job }: { job: TranscodeJob }) {
  const status = job.status;
  const stallStatus = isTrackedJob(job) ? job.stallStatus : 'normal';

  if (!status) return <span className="text-muted-foreground">-</span>;

  // Show stall indicator for processing jobs
  if (status === 'processing' && stallStatus !== 'normal') {
    return <StallIndicator stallStatus={stallStatus} />;
  }

  switch (status) {
    case 'processing':
      return <Badge variant="outline" className="border-blue-500 text-blue-600">Processing</Badge>;
    case 'success':
      return <Badge variant="outline" className="border-green-500 text-green-600">Success</Badge>;
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

function JobProgress({ progress, status, stallStatus }: {
  progress: number | null;
  status: string | null;
  stallStatus?: StallStatus;
}) {
  if (status === 'success') {
    return <span className="text-green-600 text-sm">100%</span>;
  }
  if (status === 'failed') {
    return <span className="text-red-600 text-sm">-</span>;
  }
  if (progress === null) {
    return <span className="text-muted-foreground text-sm">-</span>;
  }

  // Determine progress bar color based on stall status
  const progressBarClass = stallStatus === 'stalled'
    ? '[&>div]:bg-amber-500'
    : stallStatus === 'silent-failure'
      ? '[&>div]:bg-red-500'
      : '';

  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <Progress value={progress} className={`h-2 flex-1 ${progressBarClass}`} />
      <span className="text-sm text-muted-foreground w-10">{progress.toFixed(0)}%</span>
    </div>
  );
}

export const columns: ColumnDef<TranscodeJob>[] = [
  {
    accessorKey: 'id',
    header: 'Job ID',
    cell: ({ row }) => {
      const id = row.getValue('id') as string;
      return <CopyableCell value={id} />;
    },
    enableSorting: false,
  },
  {
    accessorKey: 'jobType',
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Type
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => row.getValue('jobType') || '-',
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <JobStatus job={row.original} />,
    enableSorting: false,
  },
  {
    accessorKey: 'progress',
    header: 'Progress',
    cell: ({ row }) => {
      const stallStatus = isTrackedJob(row.original) ? row.original.stallStatus : undefined;
      return (
        <JobProgress
          progress={row.getValue('progress')}
          status={row.original.status}
          stallStatus={stallStatus}
        />
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Created
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const value = row.getValue('createdAt') as string | null;
      return value ? new Date(value).toLocaleString() : '-';
    },
  },
  {
    accessorKey: 'clientID',
    header: 'Client ID',
    cell: ({ row }) => {
      const clientID = row.getValue('clientID') as string | null;
      if (!clientID) return '-';
      return <ClientIDCell clientID={clientID} />;
    },
    enableSorting: false,
  },
  {
    accessorKey: 'eventID',
    size: 80,
    header: 'Event ID',
    cell: ({ row }) => (
      <EventIDCell eventID={row.getValue('eventID')} />
    ),
    enableSorting: false,
  },
  {
    id: 'eventLinks',
    header: 'Links',
    cell: ({ row }) => (
      <EventLinksCell
        eventID={row.original.eventID}
        clientID={row.original.clientID}
      />
    ),
    size: 70,
    enableSorting: false,
  },
];
